/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import {
    isValidImageExtension,
    isValidDocumentExtension,
    getBasenameAndExtension
} from "./extensions";
import { extractWords } from "./extract-words";

/**
 * Auto-generates a valid filename based on the given filename.
 *
 * If no valid prefix candidate is found, the function falls back to "image" or "document" based on the extension.
 *
 * When `addTimestamp` is true, the final filename format is: [prefix]-[timestamp].[extension].
 * Otherwise, the format is: [prefix].[extension].
 *
 * @param {string} fileName - The original file name.
 * @param {boolean} [addTimestamp=false] - Whether to append a timestamp to the filename.
 * @returns {string} The generated filename.
 */
export const autoGenerateFileName = (fileName, addTimestamp = false) => {

    const [basename, ext] = getBasenameAndExtension(fileName);

    const isImg = isValidImageExtension(ext);
    const isDoc = isValidDocumentExtension(ext);
    
    // Verify that the extension is allowed.
    if (!isImg && !isDoc) {
        throw new Error("Unsupported file extension.");
    }
    
    // Determine the fallback default prefix based on the file type.
    let defaultPrefix = "";
    if (isImg) {
        defaultPrefix = "image";
    } else if (isDoc) {
        defaultPrefix = "document";
    }
    
    const prefixCandidate = extractWords(basename);
    
    // Use the candidate prefix if available; otherwise, fall back to the default.
    const prefix = prefixCandidate || defaultPrefix;

    // Construct the new base name. Append the timestamp if addTimestamp is true.
    const new_basename = addTimestamp 
        ? `${prefix}-${Date.now()}` 
        : prefix;

    // Construct the final filename.
    const new_filename = `${new_basename}.${ext}`;
    
    return new_filename;
};
